import { Component, ElementRef, OnInit, ViewChild, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from '../../../layout/service/user.service';
import { CountdownModule, CountdownComponent, CountdownConfig, CountdownEvent } from 'ngx-countdown';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { AudioService } from '../../../layout/service/audio.service';
import { EditorModule } from 'primeng/editor';


@Component({
    selector: 'app-timer',
    imports: [
        CountdownModule,
        CommonModule,
        ButtonModule,
        FormsModule,
        EditorModule
    ],
    styles: [
        `
        main:fullscreen,
        main:-webkit-full-screen,
        main:-moz-full-screen,
        main:-ms-fullscreen {
            width: 100vw !important;
            height: 100vh !important;
            background: #0f172a !important;
            overflow: auto;
        }
        `
    ],
    template: `
        <main #timerWrapper class="flex-1 overflow-auto relative">
            <!-- Fullscreen Button -->
            <button 
                (click)="toggleFullscreen()" 
                class="absolute top-6 right-6 z-50 p-3 bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700 text-white rounded-lg transition-all shadow-md flex items-center justify-center cursor-pointer"
                [title]="isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'">
                <i [class]="isFullscreen ? 'pi pi-window-minimize' : 'pi pi-window-maximize'" style="font-size: 1.2rem;"></i>
            </button>
            <div [ngClass]="isFullscreen ? 'w-full min-h-screen' : 'w-full min-h-[calc(100vh-10rem)] rounded-3xl shadow-xl'" class="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-4">
                
                <!-- Intro Screen -->
                <ng-container *ngIf="showIntroScreen">
                    <div class="max-w-4xl mx-auto text-center px-6">
                        <!-- Logo with glow effect -->
                        <div class="mb-8 flex justify-center">
                            <div class="relative flex gap-4 items-center justify-center">
                                <div>
                                    <img src="images/logo-UKNPDPD.png" alt="Logo UKNPDPD" class="w-20 h-20 object-contain">
                                </div>
                                    <img src="images/mediko-logo-white.png" alt="Mediko Logo" class="w-32 h-32 object-contain">
                            </div>
                        </div>
                        
                        <!-- Welcome Text -->
                        <h1 class="text-5xl md:text-6xl font-bold text-white mb-4">
                            Selamat Datang<br>di UKNPDPD OSCE
                        </h1>
                        
                        <p class="text-xl md:text-2xl text-amber-400 font-semibold mb-12 max-w-3xl mx-auto leading-relaxed">
                            UJI KOMPETENSI MAHASISWA PROGRAM PROFESI DOKTER<br>
                            OBJECTIVE STRUCTURED CLINICAL EXAMINATION
                        </p>
                        
                        <!-- Start Button -->
                        <button 
                            (click)="startFromIntro()"
                            class="px-12 py-4 bg-blue-600 hover:bg-blue-700 text-white text-xl font-bold rounded-xl transition-all duration-300 shadow-2xl hover:shadow-blue-500/50 hover:scale-105">
                            Mulai
                        </button>
                    </div>
                </ng-container>
                
                <!-- Category Selection Screen -->
                <ng-container *ngIf="showCategorySelection">
                    <div class="min-w-7xl mx-auto text-center">
                        <!-- Logo and Title -->
                        <div class="flex flex-col items-center mb-8">
                            <div class="bg-blue-500 p-6 rounded-3xl shadow-xl mb-6">
                                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-white">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                </svg>
                            </div>
                            <h1 class="text-4xl font-bold text-white mb-2">Timer OSCE</h1>
                            <p class="text-slate-300 text-lg">Pilih jenis timer OSCE anda</p>
                        </div>

                        <!-- Timer Cards Grid -->
                        <div class="bg-white rounded-3xl shadow-2xl p-6 mb-6">
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <!-- OSCE Nasional -->
                                <button 
                                    (click)="selectCategory('UKNPDPD')"
                                    class="min-w-7xl group bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-2xl p-4 transition-all duration-300 hover:shadow-xl hover:scale-105">
                                    <div class="flex items-center justify-between">
                                        <div class="text-left">
                                            <h3 class="text-white text-xl font-bold mb-1">OSCE Nasional</h3>
                                            <p class="text-blue-100 text-sm">Timer untuk UKNPDPD</p>
                                        </div>
                                        <div class="bg-white bg-opacity-20 p-3 rounded-xl">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                                <polyline points="9 22 9 12 15 12 15 22"></polyline>
                                            </svg>
                                        </div>
                                    </div>
                                </button>

                                <!-- OSCE Preklinik -->
                                <button 
                                    (click)="selectCategory('PREKLINIK')"
                                    class="group bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-2xl p-4 transition-all duration-300 hover:shadow-xl hover:scale-105">
                                    <div class="flex items-center justify-between">
                                        <div class="text-left">
                                            <h3 class="text-white text-xl font-bold mb-1">OSCE Preklinik</h3>
                                            <p class="text-green-100 text-sm">Timer untuk preklinik</p>
                                        </div>
                                        <div class="bg-white bg-opacity-20 p-3 rounded-xl">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                <polyline points="14 2 14 8 20 8"></polyline>
                                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                                <polyline points="10 9 9 9 8 9"></polyline>
                                            </svg>
                                        </div>
                                    </div>
                                </button>

                                <!-- OSCE Kompre -->
                                <button 
                                    (click)="selectCategory('KOAS')"
                                    style="min-width:30rem;"
                                    class="group bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-2xl p-4 transition-all duration-300 hover:shadow-xl hover:scale-105">
                                    <div class="flex items-center justify-between">
                                        <div class="text-left">
                                            <h3 class="text-white text-xl font-bold mb-1">OSCE Kompre</h3>
                                            <p class="text-orange-100 text-sm">Timer untuk KOAS</p>
                                        </div>
                                        <div class="bg-white bg-opacity-20 p-3 rounded-xl">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                                <circle cx="9" cy="7" r="4"></circle>
                                                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                            </svg>
                                        </div>
                                    </div>
                                </button>

                                <!-- Custom Timer -->
                                <button 
                                    (click)="selectCategory('Custom')"
                                    class="group bg-gradient-to-br from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-2xl p-4 transition-all duration-300 hover:shadow-xl hover:scale-105">
                                    <div class="flex items-center justify-between">
                                        <div class="text-left">
                                            <h3 class="text-white text-xl font-bold mb-1">Custom Timer</h3>
                                            <p class="text-purple-100 text-sm">Timer sesuai dengan kebutuhan</p>
                                        </div>
                                        <div class="bg-white bg-opacity-20 p-3 rounded-xl">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                                <circle cx="9" cy="7" r="4"></circle>
                                                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                            </svg>
                                        </div>
                                    </div>
                                </button>
                            </div>
                        </div>

                        <!-- Back to Home Button -->
                        <button 
                            (click)="goHome()"
                            class="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-xl transition-all duration-300 flex items-center gap-2 mx-auto shadow-lg">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                <polyline points="9 22 9 12 15 12 15 22"></polyline>
                            </svg>
                            Kembali ke Home
                        </button>
                    </div>
                </ng-container>

                <!-- Welcome Screen -->
                <ng-container *ngIf="showWelcomeScreen">
                    <div class="max-w-4xl mx-auto p-8">
                        <div class="bg-white rounded-2xl shadow-xl p-8">
                            <div class="flex items-center justify-center mb-8">
                                <div class="bg-blue-600 p-4 rounded-full">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-clock w-8 h-8 text-white">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <polyline points="12 6 12 12 16 14"></polyline>
                                    </svg>
                                </div>
                            </div>
                            <h1 class="text-3xl font-bold text-center text-gray-800 mb-2">Timer OSCE</h1>
                            <p class="text-center text-gray-600 mb-8">Sedang Memuat Timer...</p>
                            <div class="space-y-4">
                                <button *ngFor="let opt of timerOptions" 
                                        [class]="getButtonClasses(opt.key)"
                                        [disabled]="timerChoiceSelection && timerChoiceSelection !== opt.key"
                                        (click)="selectAndConfirmTimer(opt.key)">
                                    <div class="flex items-center justify-between">
                                        <div>
                                            <h3 class="text-white text-xl font-bold mb-1">{{ opt.label }}</h3>
                                            <p [class]="getDescriptionClasses(opt.key)">{{ opt.description }}</p>
                                        </div>
                                        <svg *ngIf="opt.icon === 'play'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-play w-6 h-6 group-hover:scale-110 transition-transform">
                                            <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                        </svg>
                                        <svg *ngIf="opt.icon === 'settings'" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-settings w-6 h-6 group-hover:scale-110 transition-transform">
                                            <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
                                            <circle cx="12" cy="12" r="3"></circle>
                                        </svg>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                </ng-container>

                <!-- Custom Timer Setup Screen -->
                <ng-container *ngIf="showCustomTimerSetup">
                    <div class="max-w-4xl mx-auto p-8">
                        <div class="bg-white rounded-2xl shadow-xl p-8">
                            <button class="mb-6 flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition rounded-lg border border-gray-300" (click)="goBackToWelcome()">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-rotate-ccw w-4 h-4">
                                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                    <path d="M3 3v5h5"></path>
                                </svg>
                                Kembali ke Menu
                            </button>
                            <div class="flex items-center justify-center mb-8">
                                <div class="bg-blue-600 p-4 rounded-full">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-settings w-8 h-8 text-white">
                                        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l-.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
                                        <circle cx="12" cy="12" r="3"></circle>
                                    </svg>
                                </div>
                            </div>
                            <h1 class="text-3xl font-bold text-center text-gray-800 mb-2">Custom Timer</h1>
                            <p class="text-center text-gray-600 mb-8">Atur waktu sesuai kebutuhan Anda</p>
                            <div class="space-y-6">
                                <div>
                                    <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-users w-4 h-4 mr-2">
                                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="9" cy="7" r="4"></circle>
                                            <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                        </svg>
                                        Jumlah Station
                                    </label>
                                    <input type="number" min="1" max="20" class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition" [(ngModel)]="customTimerSettings.jumlahStation" (ngModelChange)="updateScenariosArray()">
                                </div>
                                <div>
                                    <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-clock w-4 h-4 mr-2">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <polyline points="12 6 12 12 16 14"></polyline>
                                        </svg>
                                        Waktu Membaca (detik)
                                    </label>
                                    <input type="number" min="0" step="30" class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition" [(ngModel)]="customTimerSettings.waktuMembaca">
                                    <p class="text-xs text-gray-500 mt-1">Biasanya 120 detik (2 menit)</p>
                                </div>
                                <div>
                                    <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-clock w-4 h-4 mr-2">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <polyline points="12 6 12 12 16 14"></polyline>
                                        </svg>
                                        Waktu Station (detik)
                                    </label>
                                    <input type="number" min="60" step="30" class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition" [(ngModel)]="customTimerSettings.waktuStation">
                                    <p class="text-xs text-gray-500 mt-1">Biasanya 420-480 detik (7-8 menit)</p>
                                </div>
                                <div>
                                    <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-coffee w-4 h-4 mr-2">
                                            <path d="M17 8h1a4 4 0 1 1 0 8h-1"></path>
                                            <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"></path>
                                            <line x1="6" x2="6" y1="2" y2="4"></line>
                                            <line x1="10" x2="10" y1="2" y2="4"></line>
                                            <line x1="14" x2="14" y1="2" y2="4"></line>
                                        </svg>
                                        Waktu Istirahat (detik)
                                    </label>
                                    <input type="number" min="0" step="30" class="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition" [(ngModel)]="customTimerSettings.waktuIstirahat">
                                    <p class="text-xs text-gray-500 mt-1">Waktu untuk station istirahat</p>
                                </div>
                                <div>
                                    <label class="flex items-center text-sm font-semibold text-gray-700 mb-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bell w-4 h-4 mr-2">
                                            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path>
                                            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
                                        </svg>
                                        Peringatan (detik tersisa)
                                    </label>
                                    <div class="flex gap-2">
                                        <input type="number" min="0" placeholder="120" class="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition" [(ngModel)]="customTimerSettings.peringatan1">
                                        <input type="number" min="0" placeholder="60" class="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition" [(ngModel)]="customTimerSettings.peringatan2">
                                    </div>
                                    <p class="text-xs text-gray-500 mt-1">Biasanya 120 dan 60 detik (2 dan 1 menit)</p>
                                </div>
                                <div>
                                    <label class="flex items-center text-sm font-semibold text-gray-700 mb-4">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file-text w-4 h-4 mr-2">
                                            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                                            <polyline points="14,2 14,8 20,8"></polyline>
                                            <line x1="16" x2="8" y1="13" y2="13"></line>
                                            <line x1="16" x2="8" y1="17" y2="17"></line>
                                            <line x1="10" x2="8" y1="9" y2="9"></line>
                                        </svg>
                                        Skenario Kasus (Opsional)
                                    </label>
                                    <p class="text-xs text-gray-500 mb-4">Tulis skenario untuk setiap station. Jika dikosongkan, akan menggunakan skenario default.</p>
                                    <div class="space-y-4 max-h-96 overflow-y-auto">
                                        <div *ngFor="let i of [].constructor(customTimerSettings.jumlahStation); let stationIndex = index" class="border border-gray-200 rounded-lg p-4">
                                            <label class="block text-sm font-medium text-gray-700 mb-2">Station {{ stationIndex + 1 }}</label>
                                            <p-editor 
                                                [(ngModel)]="customTimerSettings.scenarios[stationIndex]" 
                                                [style]="{'height':'120px'}"
                                                placeholder="Contoh: Seorang wanita berusia 40 tahun dengan benjolan di leher, lakukan:&#10;A. Anamnesis&#10;B. Pemeriksaan Fisik&#10;C. Pemeriksaan Penunjang&#10;D. Tatalaksana farmakologis dan non farmakologis&#10;E. Edukasi">
                                                <ng-template pTemplate="header">
                                                    <span class="ql-formats">
                                                        <button class="ql-bold" aria-label="Bold"></button>
                                                        <button class="ql-italic" aria-label="Italic"></button>
                                                        <button class="ql-underline" aria-label="Underline"></button>
                                                    </span>
                                                </ng-template>
                                            </p-editor>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <button class="w-full mt-8 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition flex items-center justify-center gap-2 shadow-lg hover:shadow-xl" (click)="proceedToCustomStationSelection()">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-square-pen w-5 h-5">
                                    <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                    <path d="M18.375 2.625a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z"></path>
                                </svg>
                                Lanjut ke Pengaturan Kasus
                            </button>
                        </div>
                    </div>
                </ng-container>

                <!-- Station Configuration Screen -->
                <ng-container *ngIf="showStationConfig">
                    <div class="max-w-2xl mx-auto p-4">
                        <div class="bg-white rounded-2xl shadow-xl p-8">
                            <button class="mb-6 flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition rounded-lg border border-gray-300" (click)="goBackToWelcome()">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-rotate-ccw w-4 h-4">
                                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                    <path d="M3 3v5h5"></path>
                                </svg>
                                Kembali
                            </button>
                            <div class="flex items-center justify-center mb-8">
                                <div class="bg-blue-600 p-4 rounded-full">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-square-pen w-8 h-8 text-white">
                                        <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                        <path d="M18.375 2.625a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z"></path>
                                    </svg>
                                </div>
                            </div>
                            <h1 class="text-3xl font-bold text-center text-gray-800 mb-2">{{ selectedCategory === 'CUSTOM' ? 'Pengaturan Kasus' : 'Daftar Station' }}</h1>
                            <p class="text-center text-gray-600 mb-8">{{ selectedCategory === 'CUSTOM' ? 'Edit kasus untuk setiap station (boleh dikosongkan)' : 'Daftar station berdasarkan kategori yang dipilih' }}</p>
                            <div class="space-y-4 max-h-[500px] overflow-y-auto mb-6 pr-2">
                                <div *ngFor="let station of stations; let i = index" class="border-2 rounded-xl p-4 border-gray-200">
                                    <div class="flex items-center justify-between mb-3">
                                        <h3 class="text-lg font-bold text-gray-800">Station {{ i + 1 }}</h3>
                                        <label class="flex items-center gap-2 cursor-pointer">
                                            <input type="checkbox" class="w-4 h-4" [(ngModel)]="station.isRest">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-coffee w-4 h-4 text-green-600">
                                                <path d="M17 8h1a4 4 0 1 1 0 8h-1"></path>
                                                <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"></path>
                                                <line x1="6" x2="6" y1="2" y2="4"></line>
                                                <line x1="10" x2="10" y1="2" y2="4"></line>
                                                <line x1="14" x2="14" y1="2" y2="4"></line>
                                            </svg>
                                            <span class="text-sm font-semibold text-gray-700">Station Istirahat</span>
                                        </label>
                                    </div>
                                    <div class="mb-2">
                                        <strong>Skenario:</strong>
                                        <ng-container *ngIf="selectedCategory === 'CUSTOM'; else displayScenario">
                                            <p-editor 
                                                [(ngModel)]="station.scenario" 
                                                [style]="{'height':'120px'}"
                                                placeholder="Contoh: Seorang wanita berusia 40 tahun dengan benjolan di leher, lakukan:&#10;A. Anamnesis&#10;B. Pemeriksaan Fisik&#10;C. Pemeriksaan Penunjang&#10;D. Tatalaksana farmakologis dan non farmakologis&#10;E. Edukasi">
                                                <ng-template pTemplate="header">
                                                    <span class="ql-formats">
                                                        <button class="ql-bold" aria-label="Bold"></button>
                                                        <button class="ql-italic" aria-label="Italic"></button>
                                                        <button class="ql-underline" aria-label="Underline"></button>
                                                    </span>
                                                </ng-template>
                                            </p-editor>
                                        </ng-container>
                                        <ng-template #displayScenario>
                                            <div [innerHTML]="station.scenario || 'Tidak ada skenario'" class="mt-1 p-2 bg-gray-50 rounded border"></div>
                                        </ng-template>
                                    </div>
                                    <div>
                                        <strong>Tugas:</strong>
                                        <ng-container *ngIf="selectedCategory === 'CUSTOM'; else displayTasks">
                                            <p-editor 
                                                [(ngModel)]="station.tasks" 
                                                [style]="{'height':'100px'}"
                                                placeholder="Contoh: Lakukan anamnesis lengkap, pemeriksaan fisik thoraks, dan berikan penjelasan diagnosis">
                                                <ng-template pTemplate="header">
                                                    <span class="ql-formats">
                                                        <button class="ql-bold" aria-label="Bold"></button>
                                                        <button class="ql-italic" aria-label="Italic"></button>
                                                        <button class="ql-underline" aria-label="Underline"></button>
                                                    </span>
                                                </ng-template>
                                            </p-editor>
                                        </ng-container>
                                        <ng-template #displayTasks>
                                            <div [innerHTML]="station.tasks || 'Tidak ada tugas'" class="mt-1 p-2 bg-gray-50 rounded border"></div>
                                        </ng-template>
                                    </div>
                                </div>
                            </div>
                            <ng-container *ngIf="selectedCategory !== 'CUSTOM'">
                                <button class="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition flex items-center justify-center gap-2 shadow-lg hover:shadow-xl mb-4" (click)="startTimer()">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-play w-5 h-5">
                                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                    </svg>
                                    Mulai Timer
                                </button>
                            </ng-container>
                            <ng-container *ngIf="selectedCategory === 'CUSTOM'">
                                <button class="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition flex items-center justify-center gap-2 shadow-lg hover:shadow-xl" (click)="startTimer()">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-play w-5 h-5">
                                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                    </svg>
                                    Mulai Timer
                                </button>
                            </ng-container>
                        </div>
                    </div>
                </ng-container>
                <!-- Commented out Initial Countdown (10 seconds) -->
                <!-- <ng-container *ngIf="showInitialCountdown">
                    <div class="w-full max-w-4xl">
                        <div class="bg-white rounded-3xl shadow-2xl p-12 text-center">
                            <div class="text-9xl font-bold text-gray-800 mb-8">
                                <countdown #initialCd [config]="initialConfig" (event)="handleInitialCountdown($event)"></countdown>
                            </div>
                            <button class="px-8 py-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition shadow-lg" (click)="skipInitialCountdown()">Skip</button>
                        </div>
                    </div>
                </ng-container> -->

                <!-- Commented out Transition Countdown (30 seconds) -->
                <!-- <ng-container *ngIf="showTransitionCountdown">
                    <div class="w-full max-w-4xl">
                        <div class="bg-white rounded-3xl shadow-2xl p-12 text-center">
                            <div class="text-9xl font-bold text-gray-800 mb-8">
                                <countdown #transitionCd [config]="transitionConfig" (event)="handleTransitionCountdown($event)"></countdown>
                            </div>
                            <button class="px-8 py-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition shadow-lg" (click)="skipTransitionCountdown()">Skip</button>
                        </div>
                    </div>
                </ng-container> -->

                <!-- Main Timer -->
                <ng-container *ngIf="!showWelcomeScreen && (!showSoal || waktuIstirahat) && !showCategorySelection && !showStationConfig && !showCustomTimerSetup && !showCompletionScreen && !showIntroScreen">
                    <div class="w-full max-w-4xl mb-8">
                        <div class="flex items-center justify-between">
                            <h1 class="text-2xl font-bold text-white">OSCE Timer</h1>
                            <div class="flex items-center gap-2">
                                <button class="flex items-center gap-2 px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition" (click)="goToCategorySelection()">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-home w-4 h-4">
                                        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                        <polyline points="9,22 9,12 15,12 15,22"></polyline>
                                    </svg>
                                    Home
                                </button>
                            </div>
                        </div>
                    </div>
                    <!-- Kasus OSCE Card -->
                    <div class="w-full max-w-5xl mb-6" *ngIf="!waktuIstirahat">
                        <div class="bg-white rounded-3xl shadow-2xl p-8">
                            <h3 class="text-2xl font-bold text-cyan-500 mb-4">Kasus OSCE {{ currentStationIndex + 1 }}</h3>
                            <div class="text-gray-700" [innerHTML]="soalText"></div>
                        </div>
                    </div>

                    <!-- Info Cards (Reading Time, Station Time, Total Stations) -->
                    <div class="w-full max-w-5xl mb-6">
                        <div class="grid grid-cols-3 gap-4">
                            <div class="bg-gray-700 rounded-2xl p-4 text-center">
                                <p class="text-sm text-gray-300 mb-1">Reading Time</p>
                                <p class="text-2xl font-bold text-white">02:00</p>
                            </div>
                            <div class="bg-gray-700 rounded-2xl p-4 text-center">
                                <p class="text-sm text-gray-300 mb-1">Station Time</p>
                                <p class="text-2xl font-bold text-white">{{ Math.floor(config.leftTime / 60) }}:{{ (config.leftTime % 60).toString().padStart(2, '0') }}</p>
                            </div>
                            <div class="bg-gray-700 rounded-2xl p-4 text-center">
                                <p class="text-sm text-gray-300 mb-1">Total Stations</p>
                                <p class="text-2xl font-bold text-white">{{ stations.length }}</p>
                            </div>
                        </div>
                    </div>
                        
                    <!-- Timer Display -->
                    <div class="w-full max-w-5xl mb-6">
                        <div class="bg-white rounded-3xl shadow-2xl p-12">
                            <div class="flex justify-center items-center gap-4 mb-6">
                                <div class="bg-cyan-500 rounded-3xl p-8 min-w-[140px]">
                                    <countdown  class="text-8xl font-bold text-white text-center"#cd [config]="config" (event)="handleEvent($event)"></countdown>
                                </div>
                            </div>
                            <p class="text-3xl font-bold text-cyan-500 text-center mb-8">WAKTU TERSISA</p>
                            
                            <!-- Status Messages -->
                            <div class="mb-6" *ngIf="waktuMembacaSoal || waktuIstirahat || waktuKurangLimaMenit || waktuHabisSection || waktuHabis || (stations[currentStationIndex] && stations[currentStationIndex].isRest)">
                                <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center" *ngIf="waktuMembacaSoal || waktuKurangLimaMenit || waktuHabisSection || waktuHabis">
                                    <div *ngIf="waktuMembacaSoal" class="text-xl font-semibold text-blue-800">📖 Silahkan membaca soal</div>
                                    <div *ngIf="waktuKurangLimaMenit" class="text-xl font-semibold text-blue-800">⏰ Waktu kurang 5 menit</div>
                                    <div *ngIf="waktuHabisSection" class="text-xl font-semibold text-blue-800">➡️ Waktu habis, silakan pindah ke stasiun berikutnya</div>
                                    <div *ngIf="waktuHabis" class="text-xl font-semibold text-blue-800">✅ Ujian OSCE selesai</div>
                                </div>
                                <div class="bg-green-50 border border-green-200 rounded-lg p-4 text-center" *ngIf="(stations[currentStationIndex] && stations[currentStationIndex].isRest)">
                                    <div class="text-xl font-semibold text-green-800">🟢 Ini adalah Stasiun Istirahat</div>
                                </div>
                                <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center" *ngIf="waktuIstirahat && !(stations[currentStationIndex] && stations[currentStationIndex].isRest)">
                                    <div class="text-xl font-semibold text-blue-800">☕ Waktu istirahat</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Control Buttons -->
                    <div class="w-full max-w-5xl">
                        <div class="bg-gray-700 rounded-3xl p-6">
                            <div class="flex flex-wrap gap-4 justify-center">
                                <button class="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition shadow-lg bg-yellow-500 hover:bg-yellow-600 text-white" (click)="togglePausePlay()">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <rect width="4" height="16" x="6" y="4"></rect>
                                        <rect width="4" height="16" x="14" y="4"></rect>
                                    </svg>
                                    {{ isPaused ? 'Play' : 'Pause' }}
                                </button>
                                <button class="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition shadow-lg" [ngClass]="currentStationIndex === 0 ? 'bg-gray-400 text-gray-600 cursor-not-allowed' : 'bg-gray-500 hover:bg-gray-600 text-white'" [disabled]="currentStationIndex === 0" (click)="previousSection()">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="m12 19-7-7 7-7"></path>
                                        <path d="M19 12H5"></path>
                                    </svg>
                                    Back
                                </button>
                                <button class="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition shadow-lg" [disabled]="currentStationIndex === stations.length - 1" (click)="nextSection()">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <polygon points="5 4 15 12 5 20 5 4"></polygon>
                                        <line x1="19" x2="19" y1="5" y2="19"></line>
                                    </svg>
                                    Next Station
                                </button>
                                <button class="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition shadow-lg" (click)="resetStation()">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                        <path d="M3 3v5h5"></path>
                                    </svg>
                                    Reset Station
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Hidden countdown component -->
                    <div class="hidden">
                        <countdown #cd [config]="config" (event)="handleEvent($event)"></countdown>
                    </div>
                </ng-container>

                <!-- Soal Display -->
                <ng-container *ngIf="showSoal && !waktuIstirahat">
                    <div class="w-full max-w-5xl mx-auto mb-6">
                        <div class="bg-white rounded-2xl shadow-xl p-6 border-l-4 border-blue-600">
                            <h3 class="text-2xl font-bold text-gray-800 mb-3">Kasus OSCE {{ currentStationIndex + 1 }}</h3>
                            <div [innerHTML]="soalText"></div>
                        </div>
                    </div>
                    <div class="w-full max-w-5xl">
                        <div class="bg-white flex flex-col justify-center items-center rounded-3xl shadow-2xl p-12 text-center">
                            <div class="bg-cyan-500 rounded-3xl p-8 max-w-[20rem]">
                                <countdown class="text-8xl font-bold text-white text-center" #soalCd [config]="soalConfig" (event)="handleSoalCountdown($event)"></countdown>
                            </div>
                            <p class="text-lg text-gray-600 mb-6">Waktu Membaca Soal</p>
                            <button class="px-8 py-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition shadow-lg" (click)="skipSoalReading()">Skip Membaca</button>
                        </div>
                    </div>
                </ng-container>

                <!-- Completion Screen -->
                <ng-container *ngIf="showCompletionScreen">
                    <div class="w-full max-w-4xl mb-8">
                        <div class="flex items-center justify-between">
                            <h1 class="text-2xl font-bold text-white">OSCE Timer</h1>
                            <button class="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition" (click)="goToCategorySelection()">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-settings w-4 h-4">
                                    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l-.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                                Konfigurasi
                            </button>
                        </div>
                    </div>
                    <div class="w-full max-w-4xl">
                        <div class="bg-white rounded-3xl shadow-2xl p-12 transition-all duration-300 ring-4 ring-red-500 animate-pulse">
                            <div class="text-center">
                                <div class="mb-6">
                                    <div class="inline-block p-6 bg-green-100 rounded-full">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bell w-16 h-16 text-green-600">
                                            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path>
                                            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
                                        </svg>
                                    </div>
                                </div>
                                <h2 class="text-4xl font-bold text-gray-800 mb-4">OSCE Selesai!</h2>
                                <p class="text-xl text-gray-600 mb-8">Semua {{ stations.length }} station telah selesai</p>
                                <button class="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition flex items-center justify-center gap-2 mx-auto" (click)="goHome()">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-rotate-ccw w-5 h-5">
                                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                        <path d="M3 3v5h5"></path>
                                    </svg>
                                    Kembali ke Menu
                                </button>
                            </div>
                        </div>
                    </div>
                </ng-container>

            </div>
        </main>

        <!-- Audio Elements -->
        <!-- Timer choice modal -->
        <audio controls class="hidden" #audio1>
            <source src="sound/opening-revisi.mp3" type="audio/mp3">
        </audio>
        <audio controls class="hidden" #audio2>
            <source src="sound/pesert-ujian-menampatkan-diri.mp3" type="audio/mp3">
        </audio>
        <audio controls class="hidden" #audio3>
            <source src="sound/countdown.mp3" type="audio/mp3">
        </audio>
        <audio controls class="hidden" #audio4>
            <source src="sound/silahkan-masuk_1.mp3" type="audio/mp3">
        </audio>
        <audio controls class="hidden" #audio5>
            <source src="sound/silahkan-membaca-soal.mp3" type="audio/mp3">
        </audio>
        <audio controls class="hidden" #audio6>
            <source src="sound/waktu-habis-ujian-osce-sudah-selesai.mp3" type="audio/mp3">
        </audio>
        <audio controls class="hidden" #audio7>
            <source src="sound/waktu-habis.mp3" type="audio/mp3">
        </audio>
        <audio controls class="hidden" #audio8>
            <source src="sound/waktu-istirahat-2.mp3" type="audio/mp3">
        </audio>
        <audio controls class="hidden" #audio9>
            <source src="sound/waktu-kurang-5-menit.mp3" type="audio/mp3">
        </audio>
    `
})
export class Timer implements OnInit {
    @ViewChild('timerWrapper', { static: false }) timerWrapper!: ElementRef;
    isFullscreen: boolean = false;

    @HostListener('document:fullscreenchange', ['$event'])
    @HostListener('document:webkitfullscreenchange', ['$event'])
    @HostListener('document:mozfullscreenchange', ['$event'])
    @HostListener('document:MSFullscreenChange', ['$event'])
    onFullscreenChange() {
        this.isFullscreen = !!document.fullscreenElement;
    }

    toggleFullscreen() {
        const elem = this.timerWrapper?.nativeElement;
        if (!elem) return;

        if (!document.fullscreenElement) {
            elem.requestFullscreen().then(() => {
                this.isFullscreen = true;
            }).catch((err: any) => {
                console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
            });
        } else {
            document.exitFullscreen().then(() => {
                this.isFullscreen = false;
            });
        }
    }

    @ViewChild('cd', { static: false }) countdown!: CountdownComponent;
    @ViewChild('initialCd', { static: false }) initialCountdown!: CountdownComponent;
    @ViewChild('transitionCd', { static: false }) transitionCountdown!: CountdownComponent;
    @ViewChild('soalCd', { static: false }) soalCountdown!: CountdownComponent;
    @ViewChild('audio1') audio!: ElementRef<HTMLAudioElement>;
    @ViewChild('audio2') audio2!: ElementRef<HTMLAudioElement>;
    @ViewChild('audio3') audio3!: ElementRef<HTMLAudioElement>;
    @ViewChild('audio4') audio4!: ElementRef<HTMLAudioElement>;
    @ViewChild('audio5') audio5!: ElementRef<HTMLAudioElement>;
    @ViewChild('audio6') audio6!: ElementRef<HTMLAudioElement>;
    @ViewChild('audio7') audio7!: ElementRef<HTMLAudioElement>;
    @ViewChild('audio8') audio8!: ElementRef<HTMLAudioElement>;
    @ViewChild('audio9') audio9!: ElementRef<HTMLAudioElement>;

    // Expose Math to template
    Math = Math;

    currentUser: any;
    stations: any[] = [];
    currentStationIndex: number = 0;
    config: any = { leftTime: 0, format: 'HH:mm:ss', demand: true };
    initialConfig: any = { leftTime: 10, format: 'ss' };
    transitionConfig: any = { leftTime: 30, format: 'ss', notify: [10] };
    isPaused: boolean = true;
    showWelcomeScreen: boolean = true;
    showInitialCountdown: boolean = false;
    showTransitionCountdown: boolean = false;
    isTimerStarted: boolean = false;
    pendingSectionIndex: number | null = null;
    condition : string = 'next';
    firstLoad: boolean = true;
    waktuMembacaSoal: boolean = false;
    waktuHabis: boolean = false;
    waktuKurangLimaMenit: boolean = false;
    waktuIstirahat: boolean = false;
    waktuHabisSection: boolean = false;
    // timer selection
    showTimerChoiceDialog: boolean = true;
    timerOptions: Array<{ key: string; label: string; seconds: number; description: string; icon: string }> = [];
    timerChoiceSelection: string | null = null;
    selectedTimersPerStation: Record<string, number> = {};
    // category selection
    showCategorySelection: boolean = false;
    categoryOptions: string[] = [];
    selectedCategory: string | null = null;
    // intro screen
    showIntroScreen: boolean = false;
    // soal display
    showSoal: boolean = false;
    soalText: string = '';
    soalConfig: any = { leftTime: 120, format: 'mm:ss', demand: false };
    // station config
    showStationConfig: boolean = false;
    // custom timer setup
    showCustomTimerSetup: boolean = false;
    // completion screen
    showCompletionScreen: boolean = false;
    customTimerSettings: {
        jumlahStation: number;
        waktuMembaca: number;
        waktuStation: number;
        waktuIstirahat: number;
        peringatan1: number;
        peringatan2: number;
        scenarios: string[];
    } = {
        jumlahStation: 10,
        waktuMembaca: 120,
        waktuStation: 900,
        waktuIstirahat: 300,
        peringatan1: 180,
        peringatan2: 60,
        scenarios: []
    };

    // Build timer options for a station based on selected category
    buildTimerOptions(station: any): Array<{ key: string; label: string; seconds: number; description: string; icon: string }> {
        const opts: Array<{ key: string; label: string; seconds: number; description: string; icon: string }> = [];
        if (!station) return opts;

        const push = (key: string, label: string, value: any, description: string, icon: string) => {
            const s = Number(value || 0);
            if (s > 0) opts.push({ key, label, seconds: s, description, icon });
        };

        if (this.selectedCategory === 'UKNPDPD') {
            push('DEFAULT', 'Timer OSCE Nasional', station.TIMER, '15 menit per station + 2 menit reading', 'play');
        } else if (this.selectedCategory === 'PREKLINIK') {
            push('PREKLINIK', 'OSCE Preklinik', station.TIMER_PREKLINIK || station.TIMER_PRE, '12 menit per station + 2 menit reading', 'play');
        } else if (this.selectedCategory === 'KOAS') {
            push('KOAS', 'OSCE Kompre', station.TIMER_KOAS, '10 menit per station + 2 menit reading', 'play');
        }
        push('CUSTOM', 'Custom Your Own Time', station.TIMER_CUSTOM, 'Atur waktu sesuai kebutuhan Anda', 'settings');

        return opts;
    }


    constructor(
        private userService: UserService,
        private service: AudioService,
        private router: Router
    ) {}

    ngOnInit(): void {
        const user = localStorage.getItem('user');
        this.currentUser = user ? JSON.parse(user) : null;        

        this.userService.getSections(this.currentUser.ID).then((response) => {
            this.stations = response.data.sort((a: any, b: any) => parseInt(a.URUTAN) - parseInt(b.URUTAN));
            // Initialize station config properties
            this.stations.forEach((station, index) => {
                station.scenario = station.SOAL || '';
                station.tasks = station.TASKS || '';
                station.isRest = station.IS_REST || false;
            });
            // Extract unique categories
            const uniqueCategories = [...new Set(this.stations.flatMap(s => s.CATEGORY))];
            this.categoryOptions = [...uniqueCategories, 'Custom'];
            // Always show intro screen first
            this.showIntroScreen = false;
            this.showCategorySelection = true;
            this.showWelcomeScreen = false;
        });

    }

    startInitialCountdown(): void {
        // Before starting, ensure first station has a selected timer if multiple available
        const firstStation = this.stations[0];
        if (firstStation) {
            const opts = this.buildTimerOptions(firstStation);
            if (opts.length > 1 && !this.selectedTimersPerStation[firstStation.ID]) {
                this.timerOptions = opts;
                this.timerChoiceSelection = null;
                this.showTimerChoiceDialog = true;
                return; // wait for user selection
            } else if (opts.length === 1) {
                // Auto-select the only available timer for all stations
                const selectedOpt = opts[0];
                this.stations.forEach(station => {
                    this.selectedTimersPerStation[station.ID] = selectedOpt.seconds;
                });
            }
        }

        this.isTimerStarted = true; // Hide button immediately when clicked
        // Removed audio1 play to fix sync issues
        // this.audio.nativeElement.play();
        // this.audio.nativeElement.addEventListener('ended', () => {
            if (this.firstLoad) {
                console.log('Audio: Initial countdown removed - skipping audio3 play');
                // console.log('Audio: Playing audio3 (countdown.mp3) directly');
                // this.audio3.nativeElement.play();
                this.showWelcomeScreen = false;
                // Commented out initial 10-second countdown
                // this.showInitialCountdown = true;
                // if (this.initialCountdown) {
                //     this.initialCountdown.begin();
                //     this.firstLoad = false;
                // }
                this.firstLoad = false;
                this.startCountdown();
            }
        // });

    }

    confirmTimerChoiceForCurrentStation(): void {
        const station = this.stations[this.currentStationIndex] || this.stations[0];
        if (!station || !this.timerChoiceSelection) return;
        const opt = this.timerOptions.find(o => o.key === this.timerChoiceSelection);
        if (opt) {
            this.selectedTimersPerStation[station.ID] = opt.seconds;
        }
        this.showTimerChoiceDialog = false;
        // proceed with initial countdown if it was the first
        if (!this.isTimerStarted) {
            this.startInitialCountdown();
        } else {
            this.startCountdown();
        }
    }

    selectAndConfirmTimer(key: string): void {
        this.timerChoiceSelection = key;
        this.confirmTimerChoiceForCurrentStation();
        // After confirming, show station config
        this.showWelcomeScreen = false;
        this.showStationConfig = true;
    }

    goToTimerSelection(): void {
        this.showStationConfig = false;
        this.showWelcomeScreen = true;
    }

    selectCategory(category: string): void {
        this.selectedCategory = category === 'Custom' ? 'CUSTOM' : category;
        console.log(category);
        
        // Filter stations based on selected category, unless Custom
        if (category !== 'Custom') {            
            this.stations = this.stations.filter(s => s.CATEGORY.includes(category));
            this.showCategorySelection = false;
            this.showStationConfig = true;
        } else {
            this.showStationConfig = false;
            this.isTimerStarted = false;
            this.showCategorySelection = false;
            this.selectCustomTimer();
        }
        this.firstLoad = true;
    }

    selectCustomTimer(): void {
        // Pause any running countdown to prevent background audio
        this.isPaused = true;
        if (this.countdown) {
            this.countdown.pause();
        }
        // if (this.initialCountdown) { // Commented out - not used
        //     this.initialCountdown.pause();
        // }
        // if (this.transitionCountdown) { // Commented out - not used
        //     this.transitionCountdown.pause();
        // }
        if (this.soalCountdown) {
            this.soalCountdown.pause();
        }
        // Initialize scenarios array if not already done
        this.updateScenariosArray();
        this.showWelcomeScreen = false;
        this.showCustomTimerSetup = true;
    }

    updateScenariosArray(): void {
        // Ensure scenarios array matches jumlahStation
        if (this.customTimerSettings.scenarios.length < this.customTimerSettings.jumlahStation) {
            // Add empty scenarios for new stations
            while (this.customTimerSettings.scenarios.length < this.customTimerSettings.jumlahStation) {
                this.customTimerSettings.scenarios.push('');
            }
        } else if (this.customTimerSettings.scenarios.length > this.customTimerSettings.jumlahStation) {
            // Remove excess scenarios
            this.customTimerSettings.scenarios = this.customTimerSettings.scenarios.slice(0, this.customTimerSettings.jumlahStation);
        }
    }

    proceedToCustomStationSelection(): void {
        // Set selectedCategory to CUSTOM to show all stations
        this.selectedCategory = 'CUSTOM';
        // Apply custom settings to all stations
        this.stations.forEach((station, index) => {
            if (index < this.customTimerSettings.jumlahStation) {
                this.selectedTimersPerStation[station.ID] = this.customTimerSettings.waktuStation;
                station.isRest = false; // For now, no rest stations
                // Apply custom scenario if provided
                if (this.customTimerSettings.scenarios[index] && this.customTimerSettings.scenarios[index].trim() !== '') {
                    station.scenario = this.customTimerSettings.scenarios[index];
                } else {
                    // Use default scenario or create a placeholder
                    station.scenario = station.scenario || `Station ${index + 1} - Kasus OSCE`;
                }
            }
        });
        // Limit stations to jumlahStation
        this.stations = this.stations.slice(0, this.customTimerSettings.jumlahStation);
        // Set custom configs
        this.soalConfig.leftTime = this.customTimerSettings.waktuMembaca;
        this.transitionConfig.leftTime = this.customTimerSettings.waktuIstirahat;
        this.transitionConfig.notify = [this.customTimerSettings.peringatan1, this.customTimerSettings.peringatan2].filter(n => n > 0);
        this.showCustomTimerSetup = false;
        this.showStationConfig = true;
    }

    goBackToWelcome(): void {
        // Navigate back to category selection instead of welcome screen
        this.goToCategorySelection();
    }

    // Commented out skipInitialCountdown method (10 seconds)
    // skipInitialCountdown(): void {
    //     console.log('Audio: Pausing audio3 (countdown.mp3), playing audio4 (silahkan-masuk_1.mp3)');
    //     this.audio3.nativeElement.pause();
    //     this.audio4.nativeElement.play();
    //     this.audio4.nativeElement.addEventListener('ended', () => {
    //         console.log('Audio: audio4 ended');
    //         this.showInitialCountdown = false;
    //         this.startCountdown();
    //     });
    // }

    // Commented out skipTransitionCountdown method (30 seconds)
    // skipTransitionCountdown(): void {
    //     console.log('Audio: Skipping transition countdown, pausing audio3');
    //     this.audio3.nativeElement.pause();
    //     this.showTransitionCountdown = false;
    //     if (this.condition == 'next') {
    //         if (this.currentStationIndex < this.stations.length - 1 && !this.showTransitionCountdown) {
    //             this.pendingSectionIndex = this.currentStationIndex + 1;
    //             if (this.pendingSectionIndex !== null) {
    //                 this.currentStationIndex = this.pendingSectionIndex;
    //                 this.pendingSectionIndex = null;
    //                 this.startCountdown();
    //             }
    //         }
    //     } else {
    //         if (this.currentStationIndex > 0 && !this.showTransitionCountdown) {
    //             this.pendingSectionIndex = this.currentStationIndex - 1;
    //             if (this.pendingSectionIndex !== null) {
    //                 this.currentStationIndex = this.pendingSectionIndex;
    //                 this.pendingSectionIndex = null;
    //                 this.startCountdown();
    //             }
    //         }
    //     }
    // }

    // Commented out handleInitialCountdown method (10 seconds)
    // handleInitialCountdown(event: CountdownEvent): void {
    //     if (event.action === 'done') {
    //         console.log('Audio: Initial countdown done, pausing audio3, playing audio4');
    //         this.audio3.nativeElement.pause();
    //         this.audio4.nativeElement.play();
    //         this.audio4.nativeElement.addEventListener('ended', () => {
    //             console.log('Audio: audio4 ended');
    //             this.showInitialCountdown = false;
    //             this.startCountdown();
    //         })
    //     }
    // }

    startCountdown(): void {
        console.log(`[DEBUG] startCountdown called for station ${this.currentStationIndex + 1}, isRest: ${this.stations[this.currentStationIndex]?.isRest}, waktuIstirahat: ${this.waktuIstirahat}`);
        // Reset waktuIstirahat at the start of each station
        this.waktuIstirahat = false;
        if (this.currentStationIndex < this.stations.length) {
            const currentStation = this.stations[this.currentStationIndex];
            // ensure user selected timer if multiple options exist for this station
            const opts = this.buildTimerOptions(currentStation);
            if (opts.length > 1 && !this.selectedTimersPerStation[currentStation.ID]) {
                this.timerOptions = opts;
                this.timerChoiceSelection = null;
                this.showTimerChoiceDialog = true;
                return; // wait for user selection
            }

            // Check if station is rest station
            if (currentStation.isRest) {
                // Set timer to 5 minutes for rest station
                const leftSeconds = 300;
                // Filter notify times based on station duration
                let notifyTimes: number[] = [];
                if (leftSeconds > 10) notifyTimes.push(10);
                if (leftSeconds > 300) notifyTimes.push(300);
                this.config = { leftTime: leftSeconds, format: 'HH:mm:ss', demand: false, notify: notifyTimes };
                console.log(`[DEBUG] Config set for rest station: ${leftSeconds} seconds`);
                // Play rest audio
                console.log('Audio: Playing audio8 (waktu-istirahat-2.mp3) for rest station');
                this.audio8.nativeElement.play();
                console.log(`[DEBUG] Setting waktuIstirahat = true for rest station`);
                this.waktuIstirahat = true;
                this.audio8.nativeElement.addEventListener('ended', () => {
                    console.log('Audio: audio8 ended');
                    // Do not set waktuIstirahat = false here, keep it true during the entire rest timer
                });
                // Start the main countdown
                setTimeout(() => {
                    if (this.countdown) {
                        this.countdown.begin();
                    }
                }, 100);
                return;
            }

            // Check if station has SOAL or custom scenario
            const stationContent = currentStation.scenario || currentStation.SOAL;
            if (stationContent) {
                this.showSoal = true;
                this.soalText = stationContent;
                this.waktuMembacaSoal = true;
                // Play audio for reading soal
                console.log('Audio: Playing audio5 (silahkan-membaca-soal.mp3) for SOAL');
                this.audio5.nativeElement.play();
                this.audio5.nativeElement.addEventListener('ended', () => {
                    console.log('Audio: audio5 ended');
                    this.waktuMembacaSoal = false;
                });
                // Start 2-minute reading time
                setTimeout(() => {
                    if (this.soalCountdown) {
                        this.soalCountdown.begin();
                    }
                }, 100);
                return; // Wait for soal reading to finish
            }

            let audio_rules = JSON.parse(currentStation.AUDIO_LIST || '[]');

            if (audio_rules[0] && audio_rules[0]['1']) {
                console.log('Audio: Playing audio5 based on audio_rules[0][1]');
                this.audio5.nativeElement.play();
                this.waktuMembacaSoal = true;
                this.audio5.nativeElement.addEventListener('ended', () => {
                    console.log('Audio: audio5 ended');
                    this.waktuMembacaSoal = false;
                })
            } else if (audio_rules[0] && audio_rules[0]['5']) {
                console.log('Audio: Playing audio8 based on audio_rules[0][5]');
                this.audio8.nativeElement.play();
                this.waktuIstirahat = true;
                this.audio8.nativeElement.addEventListener('ended', () => {
                    console.log('Audio: audio8 ended');
                    this.waktuIstirahat = false;
                })
            }

            const leftSeconds = this.selectedTimersPerStation[currentStation.ID] ?? Number(currentStation.TIMER || 0);
            // Set notify based on custom settings if applicable
            let notifyTimes: number[] = [];
            if (this.selectedCategory === 'CUSTOM') {
                notifyTimes = [this.customTimerSettings.peringatan2 * 1000, this.customTimerSettings.peringatan1 * 1000].filter(n => n > 0 && n < leftSeconds * 1000);
            } else {
                // Only add notify times that are less than the station duration
                if (leftSeconds > 10) notifyTimes.push(10);
                if (leftSeconds > 300) notifyTimes.push(300);
            }
            this.config = { leftTime: Number(leftSeconds), format: 'HH:mm:ss', demand: false, notify: notifyTimes };
            // Start the main countdown
            setTimeout(() => {
                if (this.countdown) {
                    this.countdown.begin();
                }
            }, 100);
        }
    }

    handleEvent(event: CountdownEvent): void {
        if (event.action == 'notify' ) {
            if (this.selectedCategory === 'CUSTOM') {
                if (event.left == this.customTimerSettings.peringatan2 * 1000) {
                    console.log(`Audio: Custom notify at ${this.customTimerSettings.peringatan2} seconds, playing audio3 (countdown.mp3)`);
                    // Prevent overlapping audio
                    if (!this.audio3.nativeElement.paused) {
                        this.audio3.nativeElement.pause();
                    }
                    this.audio3.nativeElement.currentTime = 0;
                    this.audio3.nativeElement.play();
                } else if (event.left == this.customTimerSettings.peringatan1 * 1000 && !this.waktuIstirahat) {
                    console.log(`Audio: Custom notify at ${this.customTimerSettings.peringatan1} seconds, playing audio9 (waktu-kurang-5-menit.mp3)`);
                    // Prevent overlapping audio
                    if (!this.audio9.nativeElement.paused) {
                        this.audio9.nativeElement.pause();
                    }
                    this.audio9.nativeElement.currentTime = 0;
                    this.audio9.nativeElement.play();
                    this.waktuKurangLimaMenit = true;
                    this.audio9.nativeElement.addEventListener('ended', () => {
                        console.log('Audio: audio9 ended');
                        this.waktuKurangLimaMenit = false;
                    });
                }
            } else {
                if (event.left == 10000) {
                    console.log('Audio: Notify at 10 seconds, playing audio3 (countdown.mp3)');
                    // Prevent overlapping audio
                    if (!this.audio3.nativeElement.paused) {
                        this.audio3.nativeElement.pause();
                    }
                    this.audio3.nativeElement.currentTime = 0;
                    this.audio3.nativeElement.play();
                } else if (event.left == 300000 && !this.waktuIstirahat) {
                    console.log('Audio: Notify at 5 minutes, playing audio9 (waktu-kurang-5-menit.mp3)');
                    // Prevent overlapping audio
                    if (!this.audio9.nativeElement.paused) {
                        this.audio9.nativeElement.pause();
                    }
                    this.audio9.nativeElement.currentTime = 0;
                    this.audio9.nativeElement.play();
                    this.waktuKurangLimaMenit = true;
                    this.audio9.nativeElement.addEventListener('ended', () => {
                        console.log('Audio: audio9 ended');
                        this.waktuKurangLimaMenit = false;
                    });
                }
            }
        }
        if (event.action === 'done') {
            const currentStation = this.stations[this.currentStationIndex];
            let audio_rules = JSON.parse(currentStation.AUDIO_LIST || '[]');

            console.log(`[DEBUG] handleEvent done for station ${this.currentStationIndex + 1}, isRest: ${currentStation.isRest}, waktuIstirahat: ${this.waktuIstirahat}`);
            console.log('Audio: Timer done, pausing audio3');
            this.audio3.nativeElement.pause();
            
            // Always reset waktuIstirahat when moving to next station or completing
            console.log(`[DEBUG] Resetting waktuIstirahat to false`);
            this.waktuIstirahat = false;
            
            // Check if this is the last station
            if (this.currentStationIndex === this.stations.length - 1) {
                console.log('Audio: Last station completed, showing completion screen');
                this.showCompletionScreen = true;
                // Pause any running countdowns
                this.isPaused = true;
                if (this.countdown) {
                    this.countdown.pause();
                }
                return;
            }
            
            // For rest stations, go directly to next station without audio
            if (currentStation.isRest) {
                console.log('Audio: Rest station completed, going to next station');
                // Stop the current countdown
                console.log(`[DEBUG] Stopping countdown for rest station transition`);
                if (this.countdown) {
                    this.countdown.stop();
                }
                this.currentStationIndex++;
                this.startCountdown();
                return;
            }
            
            if (audio_rules[0] && audio_rules[0]['4']) {
                console.log('Audio: Playing audio7 (waktu-habis.mp3) based on audio_rules[0][4]');
                this.audio7.nativeElement.play();
                this.waktuHabisSection = true;
                this.audio7.nativeElement.addEventListener('ended', () => {
                    console.log('Audio: audio7 ended');
                    // Commented out transition countdown (30 seconds)
                    // this.startTransitionCountdown();
                    this.waktuHabisSection = false;
                    // Go directly to next station
                    if (this.currentStationIndex < this.stations.length - 1) {
                        // Stop the current countdown
                        console.log(`[DEBUG] Stopping countdown for normal station transition`);
                        if (this.countdown) {
                            this.countdown.stop();
                        }
                        this.currentStationIndex++;
                        this.startCountdown();
                    }
                })
            } else {
                console.log('Audio: Playing audio6 (waktu-habis-ujian-osce-sudah-selesai.mp3)');
                this.audio6.nativeElement.play();
                this.waktuHabis = true;
            }

        }
    }

    handleSoalCountdown(event: CountdownEvent): void {
        if (event.action === 'done') {
            this.skipSoalReading();
        }
    }

    skipSoalReading(): void {
        console.log('Audio: Skipping soal reading');
        this.showSoal = false;
        this.waktuMembacaSoal = false;
        // Now start the actual timer
        const currentStation = this.stations[this.currentStationIndex];
        let audio_rules = JSON.parse(currentStation.AUDIO_LIST || '[]');

        if (audio_rules[0] && audio_rules[0]['1']) {
            console.log('Audio: Playing audio5 based on audio_rules[0][1]');
            this.audio5.nativeElement.play();
            this.waktuMembacaSoal = true;
            this.audio5.nativeElement.addEventListener('ended', () => {
                console.log('Audio: audio5 ended');
                this.waktuMembacaSoal = false;
            })
        }

        const leftSeconds = this.selectedTimersPerStation[currentStation.ID] ?? Number(currentStation.TIMER || 0);
        // Filter notify times based on station duration
        let notifyTimes: number[] = [];
        if (leftSeconds > 10) notifyTimes.push(10);
        if (leftSeconds > 300) notifyTimes.push(300);
        this.config = { leftTime: Number(leftSeconds), format: 'HH:mm:ss', demand: false , notify: notifyTimes};
        // Start the main countdown
        setTimeout(() => {
            if (this.countdown) {
                this.countdown.begin();
            }
        }, 100);
    }

    // Commented out transition countdown methods (30 seconds)
    // startTransitionCountdown(): void {
    //     this.showTransitionCountdown = true;
    //     if (this.transitionCountdown) {
    //         this.transitionCountdown.begin();
    //     }
    // }

    // handleTransitionCountdown(event: CountdownEvent): void {
    //     if (event.action === 'notify') {
    //         if (event.left == 10000) {
    //             console.log('Audio: Transition countdown notify at 10 seconds, playing audio3');
    //             this.audio3.nativeElement.play();
    //         }
    //     }
    //     if (event.action === 'done') {
    //         console.log('Audio: Transition countdown done, pausing audio3');
    //         this.audio3.nativeElement.pause();
    //         this.showTransitionCountdown = false;
    //         if (this.condition == 'next') {
    //             if (this.currentStationIndex < this.stations.length - 1 && !this.showTransitionCountdown) {
    //                 this.pendingSectionIndex = this.currentStationIndex + 1;
    //                 if (this.pendingSectionIndex !== null) {
    //                     this.currentStationIndex = this.pendingSectionIndex;
    //                     this.pendingSectionIndex = null;
    //                     this.startCountdown();
    //                 }
    //             }
    //         } else {
    //             if (this.currentStationIndex > 0 && !this.showTransitionCountdown) {
    //                 this.pendingSectionIndex = this.currentStationIndex - 1;
    //                 if (this.pendingSectionIndex !== null) {
    //                     this.currentStationIndex = this.pendingSectionIndex;
    //                     this.pendingSectionIndex = null;
    //                     this.startCountdown();
    //                 }
    //             }
    //         }
    //     }
    // }

    nextSection(): void {
        this.condition = 'next';
        if (this.currentStationIndex < this.stations.length - 1) {
            this.pendingSectionIndex = this.currentStationIndex + 1;
            // Commented out transition countdown (30 seconds) - go directly to next station
            // if (this.transitionConfig.leftTime > 0) {
            //     this.showTransitionCountdown = true;
            //     if (this.transitionCountdown) {
            //         this.transitionCountdown.begin();
            //     }
            // } else {
                // No transition time, go directly to next station
                this.currentStationIndex = this.pendingSectionIndex;
                this.pendingSectionIndex = null;
                this.startCountdown();
            // }
        }
    }

    previousSection(): void {
        this.condition = 'previous';
        if (this.currentStationIndex > 0) {
            this.pendingSectionIndex = this.currentStationIndex - 1;
            // Commented out transition countdown (30 seconds) - go directly to previous station
            // if (this.transitionConfig.leftTime > 0) {
            //     this.showTransitionCountdown = true;
            //     if (this.transitionCountdown) {
            //         this.transitionCountdown.begin();
            //     }
            // } else {
                // No transition time, go directly to previous station
                this.currentStationIndex = this.pendingSectionIndex;
                this.pendingSectionIndex = null;
                this.startCountdown();
            // }
        }
    }

    // goBackToWelcome(): void {
    //     this.showStationConfig = false;
    //     this.showWelcomeScreen = true;
    // }

    startTimer(): void {
        // Show intro screen before starting timer
        this.showStationConfig = false;
        this.showIntroScreen = true;
    }

    resetStation(): void {
        // Reset current station timer
        if (this.countdown) {
            this.countdown.stop();
        }
        const currentStation = this.stations[this.currentStationIndex];
        if (currentStation) {
            if (currentStation.isRest) {
                // For rest station, just reset the timer
                const leftSeconds = 300; // 5 minutes for rest
                // Filter notify times based on station duration
                let notifyTimes: number[] = [];
                if (leftSeconds > 10) notifyTimes.push(10);
                if (leftSeconds > 300) notifyTimes.push(300);
                this.config = { leftTime: leftSeconds, format: 'HH:mm:ss', demand: false, notify: notifyTimes };
                setTimeout(() => {
                    if (this.countdown) {
                        this.countdown.begin();
                    }
                }, 100);
            } else {
                // For non-rest station, start from reading soal
                this.showSoal = true;
                this.soalText = currentStation.SOAL;
                this.waktuMembacaSoal = true;
                // Play audio for reading soal
                console.log('Audio: Reset - Playing audio5 (silahkan-membaca-soal.mp3) for SOAL');
                this.audio5.nativeElement.play();
                this.audio5.nativeElement.addEventListener('ended', () => {
                    console.log('Audio: audio5 ended');
                    this.waktuMembacaSoal = false;
                });
                // Start 2-minute reading time
                setTimeout(() => {
                    if (this.soalCountdown) {
                        this.soalCountdown.begin();
                    }
                }, 100);
            }
        }
        this.isPaused = false;
        this.waktuIstirahat = false;
        this.waktuKurangLimaMenit = false;
        this.waktuHabisSection = false;
    }

    getCategoryDescriptionClasses(category: string): string {
        if (category === 'UKNPDPD') {
            return 'text-blue-100 text-sm';
        } else if (category === 'PREKLINIK') {
            return 'text-green-100 text-sm';
        } else if (category === 'KOAS') {
            return 'text-orange-100 text-sm';
        } else if (category === 'Custom') {
            return 'text-slate-100 text-sm';
        }
        return 'text-gray-100 text-sm';
    }

    getCategoryLabel(category: string): string {
        if (category === 'UKNPDPD') {
            return 'Timer OSCE Nasional';
        } else if (category === 'PREKLINIK') {
            return 'OSCE Preklinik';
        } else if (category === 'KOAS') {
            return 'OSCE Kompre';
        } else if (category === 'Custom') {
            return 'Custom Timer';
        }
        return category;
    }

    getCategoryButtonClasses(category: string): string {
        let base = 'w-full p-6 text-white rounded-xl transition shadow-lg hover:shadow-xl text-left group ';
        if (category === 'UKNPDPD') {
            base += 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700';
        } else if (category === 'PREKLINIK') {
            base += 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700';
        } else if (category === 'KOAS') {
            base += 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700';
        } else if (category === 'Custom') {
            base += 'bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700';
        } else {
            base += 'bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700';
        }
        return base;
    }

    togglePausePlay(): void {
        if (this.countdown) {
            if (this.isPaused) {
                this.countdown.begin();
            } else {
                this.countdown.pause();
            }
            this.isPaused = !this.isPaused;
        }
    }

    startFromIntro(): void {
        this.showIntroScreen = false;
        // Start the timer countdown
        this.startInitialCountdown();
    }

    goHome(): void {
        // Navigate to dashboard
        this.router.navigate(['/app/dashboard']);
    }

    goToCategorySelection(): void {
        // Pause any running countdown before going to category selection
        this.isPaused = true;
        if (this.countdown) {
            this.countdown.pause();
        }
        // if (this.initialCountdown) { // Commented out - not used
        //     this.initialCountdown.pause();
        // }
        // if (this.transitionCountdown) { // Commented out - not used
        //     this.transitionCountdown.pause();
        // }
        if (this.soalCountdown) {
            this.soalCountdown.pause();
        }
        // Reset to category selection screen
        this.showWelcomeScreen = false;
        // this.showInitialCountdown = false; // Commented out - not used
        // this.showTransitionCountdown = false; // Commented out - not used
        this.showSoal = false;
        this.showIntroScreen = false;
        this.showCategorySelection = true;
        this.showStationConfig = false;
        this.showCustomTimerSetup = false;
        this.showCompletionScreen = false;
        this.currentStationIndex = 0;
        this.isTimerStarted = false;
        this.firstLoad = true;
        this.isPaused = true;
        this.waktuMembacaSoal = false;
        this.waktuHabis = false;
        this.waktuKurangLimaMenit = false;
        this.waktuIstirahat = false;
        this.waktuHabisSection = false;
        // Stop any running countdowns
        if (this.countdown) {
            this.countdown.stop();
        }
        if (this.initialCountdown) {
            this.initialCountdown.stop();
        }
        if (this.transitionCountdown) {
            this.transitionCountdown.stop();
        }
        if (this.soalCountdown) {
            this.soalCountdown.stop();
        }
    }
}
